import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EmptyState } from '@ui-kit';
import { ThemeToggle } from '../../_shared/ThemeToggle';
import { LanguageToggle } from '../../_shared/LanguageToggle';
import { APP_VERSION } from '../../_shared/appVersion';
import { usePasswordResetRequest } from '../hooks/usePasswordResetRequest';
import {
  passwordResetRequestSchema,
  type PasswordResetRequestFormValues,
} from '../schema/passwordReset.schema';

// ─── Toast ──────────────────────────────────────────────────────────────────

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  const { t } = useTranslation();
  useEffect(() => {
    const id = setTimeout(onClose, 5000);
    return () => clearTimeout(id);
  }, [onClose]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'fixed',
        top: 'var(--spacing-6)',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-success)',
        borderLeft: '4px solid var(--color-success)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        maxWidth: '480px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-3)',
        boxShadow: 'var(--shadow-md)',
        zIndex: 2000,
        width: 'calc(100vw - var(--spacing-8))',
      }}
    >
      <span
        aria-hidden="true"
        style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-success)', flexShrink: 0 }}
      >
        ✓
      </span>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--spacing-1)',
          }}
        >
          {t('pwReset.request.toast.sentTitle')}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {message}
        </div>
      </div>
    </div>
  );
}

// ─── PasswordResetRequestPage ─────────────────────────────────────────────

export function PasswordResetRequestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mutation = usePasswordResetRequest();

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetRequestFormValues>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: { email: '' },
  });

  const isPending = mutation.isPending;
  const isRateLimit =
    mutation.isError &&
    (mutation.error as { code?: string })?.code === 'HTTP-429';

  const onSubmit = useCallback(
    (values: PasswordResetRequestFormValues) => {
      mutation.mutate(values.email, {
        onSuccess: () => {
          setSubmitted(true);
          setToastMessage(t('pwReset.request.toast.sent'));
        },
      });
    },
    [mutation]
  );

  const handleBackToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-surface-background)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-8)',
        position: 'relative',
      }}
      aria-busy={isPending}
    >
      {/* Toast */}
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}

      {/* Auth Card */}
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
          padding: 'var(--spacing-8)',
          width: '100%',
          maxWidth: '420px',
          position: 'relative',
        }}
        role="main"
        aria-label={t('pwReset.request.formAriaLabel')}
      >
        {/* Theme + Language toggles */}
        <div
          style={{
            position: 'absolute',
            top: 'var(--spacing-4)',
            right: 'var(--spacing-4)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          <LanguageToggle size="sm" />
          <ThemeToggle />
        </div>

        {/* Logo */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: '48px',
              height: '48px',
              background: 'var(--color-brand-primary)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-inverse)',
              fontWeight: 800,
              fontSize: 'var(--font-size-lg)',
              marginRight: 'var(--spacing-3)',
              flexShrink: 0,
            }}
          >
            E
          </div>
          <span
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 800,
              color: 'var(--color-brand-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            eraf
          </span>
        </div>

        {/* Title */}
        <h1
          style={{
            textAlign: 'center',
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            margin: '0 0 var(--spacing-2)',
          }}
        >
          {t('pwReset.request.title')}
        </h1>
        <p
          style={{
            textAlign: 'center',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--spacing-6)',
            lineHeight: 1.55,
          }}
        >
          {t('pwReset.request.subtitle')}
        </p>

        {/* ── Empty state: rate-limit (429) ── */}
        {isRateLimit && (
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <EmptyState
              title={t('pwReset.request.empty.rateLimit')}
              description={`(${t('pwReset.request.empty.rateLimitCode')})`}
            />
          </div>
        )}

        {/* ── Success info banner (after submission) ── */}
        {submitted && !isRateLimit && (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: 'color-mix(in srgb, var(--color-success) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-success)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-3) var(--spacing-4)',
              marginBottom: 'var(--spacing-4)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.6,
              textAlign: 'center',
            }}
          >
            <span style={{ fontWeight: 600, color: 'var(--color-success)' }}>
              {t('pwReset.request.banner.sentTitle')}
            </span>
            <br />
            {t('pwReset.request.toast.sent')}
            <br />
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              {t('pwReset.request.banner.checkSpam')}
            </span>
          </div>
        )}

        {/* ── Error banner (non-rate-limit) ── */}
        {mutation.isError && !isRateLimit && (
          <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            id="pw-reset-error"
            style={{
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-3) var(--spacing-4)',
              marginBottom: 'var(--spacing-4)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--spacing-3)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontSize: 'var(--font-size-lg)',
                flexShrink: 0,
                lineHeight: 1.4,
                color: 'var(--color-danger)',
              }}
            >
              ✕
            </span>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--color-danger)',
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                {t('pwReset.request.error.title')}
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                {(mutation.error as { message?: string })?.message ?? t('pwReset.request.error.generic')}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          aria-describedby={mutation.isError ? 'pw-reset-error' : undefined}
        >
          {/* Email field */}
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label
              htmlFor="pw-reset-email"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('pwReset.request.email.label')}
            </label>
            <input
              id="pw-reset-email"
              type="email"
              autoComplete="email"
              placeholder="user@skax.com"
              disabled={isPending || isRateLimit}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'pw-reset-email-error' : undefined}
              {...register('email')}
              style={{
                display: 'block',
                width: '100%',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background:
                  isPending || isRateLimit
                    ? 'var(--color-surface-muted)'
                    : 'var(--color-surface-elevated)',
                border: `1px solid ${errors.email ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-base)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-sans)',
                outline: 'none',
                opacity: isPending || isRateLimit ? 0.5 : 1,
                cursor: isPending || isRateLimit ? 'not-allowed' : 'text',
                minHeight: '38px',
              }}
            />
            {errors.email && (
              <span
                id="pw-reset-email-error"
                role="alert"
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-danger)',
                  marginTop: 'var(--spacing-1)',
                }}
              >
                {errors.email.message === 'pwReset.request.email.required'
                  ? t('pwReset.request.email.required')
                  : errors.email.message === 'pwReset.request.email.invalid'
                  ? t('pwReset.request.email.invalid')
                  : errors.email.message === 'pwReset.request.email.tooLong'
                  ? t('pwReset.request.email.tooLong')
                  : errors.email.message}
              </span>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isPending || isRateLimit}
            aria-label={isPending ? t('pwReset.request.button.submitting') : t('pwReset.request.button.submit')}
            aria-disabled={isPending || isRateLimit}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-2)',
              width: '100%',
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 600,
              cursor: isPending || isRateLimit ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              marginBottom: 'var(--spacing-4)',
              opacity: isPending || isRateLimit ? 0.6 : 1,
            }}
          >
            {isPending && (
              <span
                aria-hidden="true"
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid var(--color-border-subtle)',
                  borderTopColor: 'var(--color-text-inverse)',
                  borderRadius: 'var(--radius-full)',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                  flexShrink: 0,
                }}
              />
            )}
            {isPending ? t('pwReset.request.button.submitting') : t('pwReset.request.button.submit')}
          </button>
        </form>

        {/* Back to login */}
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={handleBackToLogin}
            aria-label={t('pwReset.request.button.backToLogin')}
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-brand-primary)',
              cursor: 'pointer',
              textDecoration: 'none',
              background: 'none',
              border: 'none',
              fontFamily: 'var(--font-family-sans)',
              padding: 0,
            }}
          >
            ← {t('pwReset.request.button.backToLogin')}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div
        aria-label={t('pwReset.footer.aria')}
        style={{
          marginTop: 'var(--spacing-6)',
          display: 'flex',
          gap: 'var(--spacing-4)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          whiteSpace: 'nowrap' as const,
          flexWrap: 'wrap' as const,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <span>v{APP_VERSION}</span>
        <span>{t('pwReset.footer.copyright')}</span>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 767px) {
          form input { font-size: var(--font-size-sm); }
        }
      `}</style>
    </div>
  );
}
